/**
 * Single source of truth for "is MSW actually intercepting in this build?".
 *
 * `main.tsx` uses `mswActive()` to decide whether to start the worker and calls
 * `markMswWorkerStarted()` once `startMsw()` resolves. UI that only makes sense
 * against mocked endpoints (e.g. the Data Explorer's fixture `demo-*` uploads)
 * gates on `dataVizMswActive()`, which checks the worker ACTUALLY started — so a
 * feature flag alone can't surface mock-only UI when MSW isn't running (a
 * production bundle, dev with `VITE_USE_MSW=false`, or a worker that failed to
 * register and fell through to the network).
 */

/**
 * Build-time constant: `vite --mode demo` / `vite build --mode demo`.
 *
 * The frontend-only demo build. Everything is mocked, the app lands signed in,
 * and the tour UI mounts. Gated on the Vite MODE like offline mode, so it cannot
 * be switched on from `.env.local` or reach the dev/main deploy bundles (which
 * build in `production` mode). See `apps/web/.env.demo`.
 */
export const DEMO_MODE = import.meta.env.MODE === "demo";

// Set true by main.tsx after `startMsw()` resolves. A module-level mutable flag
// (not derived from env) because env says whether we INTEND to mock; this says
// whether the worker is actually intercepting.
let mswWorkerStarted = false;

/** Called by main.tsx after the MSW worker has successfully started. */
export function markMswWorkerStarted(): void {
  mswWorkerStarted = true;
}

/**
 * Whether the MSW worker will start. Mirrors the gating documented in
 * `main.tsx`: production is opt-in (`VITE_USE_MSW=true` + a localhost API base),
 * dev is opt-out (on unless `VITE_USE_MSW=false`).
 */
export function mswActive(): boolean {
  // Demo builds are PROD builds with a relative API base (`/api/v2/compliance`),
  // which the localhost rule below would refuse — and MSW *is* the backend there.
  if (DEMO_MODE) return true;
  const flag = import.meta.env.VITE_USE_MSW;
  if (import.meta.env.PROD) {
    if (flag !== "true") return false;
    // Defense-in-depth: refuse to mock against a non-local backend.
    const base = import.meta.env.VITE_API_BASE_URL ?? "";
    try {
      const { hostname } = new URL(base);
      return hostname === "127.0.0.1" || hostname === "localhost";
    } catch {
      return false;
    }
  }
  // Dev mode: opt-out.
  return flag !== "false";
}

/**
 * Whether the data-viz MSW handlers (`/data/*`, `/uploads/{id}/rows`) are
 * intercepting — the worker actually STARTED AND the per-feature flag is on.
 * Mirrors `USE_DATA_MSW` in `mocks/handlers.ts`. Always true under test.
 *
 * Call this at render time (not module scope): the worker-started flag is set
 * during bootstrap, after this module is first imported.
 */
export function dataVizMswActive(): boolean {
  if (import.meta.env.MODE === "test") return true;
  return mswWorkerStarted && import.meta.env.VITE_USE_DATA_MSW === "true";
}

/** Sister demos the header links to; empty outside the demo build. */
export const DEMO_LINKS: ReadonlyArray<{ label: string; href: string; tour: string }> = DEMO_MODE
  ? [
      {
        label: "Portal demo",
        href: import.meta.env.VITE_DEMO_PORTAL_URL ?? "",
        tour: "portal-link",
      },
      {
        label: "Chatbot demo",
        href: import.meta.env.VITE_DEMO_CHATBOT_URL ?? "",
        tour: "chatbot-link",
      },
    ].filter((l) => l.href)
  : [];
